import React, { useMemo } from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { appendUrlSearchParams, navigate } from "../../../../hooks/navigation";
import { IconPredefined } from "../../icons/Icon";
import { PredefinedIconType } from "../../icons/Icon/Predefined";
import { TabBar, TabbedControlled, TabContent } from "../../Tabs";
import ElectricityCalculator from "./ElectricityCalculator";
import GasCalculator from "./GasCalculator";
import HeatingOilCalculator from "./HeatingOilCalculator";
import PelletsCalculator from "./PelletsCalculator";
import { CalculatorProvider } from "./provider";

const fontStyles = css`
    font-size: 1rem;
`;

const Container = styled(TabbedControlled)`
    flex-direction: column;
    max-width: 800px;
    margin: 0 auto;

    ${({ theme }) => theme.breakpoints.upTo.small.css`
       ${theme.paddings.containerInner.left}
       ${theme.paddings.containerInner.right}
       ${fontStyles}
    `}

    ${TabBar} {
        flex-direction: row;
        column-gap: 0;
        justify-content: space-around;
        max-width: 100%;

        & button {
            z-index: 2;
        }
    }

    ${TabContent} {
        margin-top: 0;
    }
` as typeof TabbedControlled;

export enum CalculatorType {
    "heatingOil" = "heatingOil",
    "electricity" = "electricity",
    "gas" = "gas",
    "pellets" = "pellets",
    "adBlue" = "adBlue",
    "oilfox" = "oilfox",
    "technik" = "technik",
}

export type CmsPriceCalculatorProps<tab extends CalculatorType> = {
    types: tab[] & { 0: tab };
    buttonLabel: string;
    queryString: string;
};

const restrict = <r extends Record<string, any>, target extends keyof r>(
    r: r,
    ks: target[]
) =>
    Object.fromEntries(
        Object.entries(r).filter(([k]) => ks.includes(k as target))
    ) as {
        [k in target]: r[k];
    };

const CmsPriceCalculator = <tab extends CalculatorType>({
    types,
    buttonLabel,
    queryString,
}: CmsPriceCalculatorProps<tab>) => {
    const theme = useTheme();

    const shopTabStyles = css`
        display: flex;
        ${theme.breakpoints.from.medium.css`
            gap: 5px;
        `}

        align-items: center;

        &:hover svg {
            fill: ${theme.colors.red.toString()};
        }
    `;

    const shopIconStyles = css`
        margin-top: -10px;
    `;

    const query = useMemo(
        () => new URLSearchParams(queryString),
        [queryString]
    );

    const tabs = useMemo(
        () =>
            restrict(
                {
                    [CalculatorType.heatingOil]: <>Heizöl</>,
                    [CalculatorType.pellets]: <>Pellets</>,
                    [CalculatorType.electricity]: <>Strom</>,
                    [CalculatorType.gas]: <>Erdgas</>,
                    [CalculatorType.adBlue]: (
                        <div css={shopTabStyles}>
                            <IconPredefined
                                type={PredefinedIconType.shop}
                                color={theme.colors.blue.toString()}
                                css={shopIconStyles}
                            />
                            <span>AdBlue®</span>
                        </div>
                    ),
                    [CalculatorType.oilfox]: (
                        <div css={shopTabStyles}>
                            <IconPredefined
                                type={PredefinedIconType.shop}
                                color={theme.colors.blue.toString()}
                                css={shopIconStyles}
                            />
                            <span>Oilfox</span>
                        </div>
                    ),
                    [CalculatorType.technik]: (
                        <div css={shopTabStyles}>
                            <IconPredefined
                                type={PredefinedIconType.shop}
                                color={theme.colors.blue.toString()}
                                css={shopIconStyles}
                            />
                            <span>Technik</span>
                        </div>
                    ),
                },
                types
            ),
        [types, shopTabStyles, shopIconStyles, theme.colors.blue]
    );
    const contents = useMemo(
        () =>
            restrict(
                {
                    heatingOil: <HeatingOilCalculator />,
                    pellets: <PelletsCalculator />,
                    electricity: <ElectricityCalculator />,
                    gas: <GasCalculator />,
                    adBlue: null,
                    oilfox: null,
                    technik: null,
                },
                types
            ),
        [types]
    );

    const onTabSelect = (tab: CalculatorType) => {
        const target = (
            {
                [CalculatorType.adBlue]: "/adblue",
                [CalculatorType.oilfox]: "/oilfox",
                [CalculatorType.technik]: "/technik",
            } as Partial<Record<CalculatorType, string>>
        )[tab];

        if (target) void navigate(appendUrlSearchParams(target, query));
    };

    return (
        <CalculatorProvider value={{ query }}>
            <Container defaultSelected={types[0]} {...{ tabs, onTabSelect }}>
                {contents}
            </Container>
        </CalculatorProvider>
    );
};

export default CmsPriceCalculator;
